import { useRef, useState, useEffect } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import SectionLabel from "../ui/SectionLabel";
import SplitHeading from "../ui/SplitHeading";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";
import SectionSubtitle from "../ui/SectionSubtitle";

const TRIGGER = 0.68;

function StepItem({ step, isOpen, stepRef, instant = false }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={stepRef}
      data-testid={`process-step-${step.n}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: "relative", overflow: "hidden", borderBottom: "1px solid rgba(242,232,216,0.06)", padding: "3rem 0" }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute", right: "-1rem", top: "50%", transform: "translateY(-50%)",
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: "clamp(5rem, 10vw, 9rem)", fontWeight: 700,
          color: isOpen ? "rgba(124,196,216,0.07)" : "rgba(242,232,216,0.04)",
          lineHeight: 1, pointerEvents: "none", userSelect: "none", zIndex: 0,
          transition: "color 0.6s ease",
        }}
      >
        {step.n}
      </div>

      <div aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, width: 2, height: "100%", zIndex: 1 }}>
        <motion.div
          animate={{ scaleY: isOpen ? 1 : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", background: "#7CC4D8", transformOrigin: "top", boxShadow: "0 0 12px rgba(124,196,216,0.4)" }}
        />
        <motion.div
          animate={{ opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.3, delay: isOpen ? 0.2 : 0 }}
          style={{ position: "absolute", left: -3, top: 0, width: 8, height: 8, borderRadius: "50%", background: "#7CC4D8", boxShadow: "0 0 8px #7CC4D8" }}
        />
      </div>

      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "2rem", paddingLeft: "2rem" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: "0.7rem", color: "#7CC4D8", opacity: isOpen ? 1 : 0.5, letterSpacing: "0.2em", transition: "opacity 0.4s ease", flexShrink: 0 }}>
              {step.n}
            </span>
            <h3
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 300, color: "#F2E8D8",
                transform: isOpen || hovered ? "translateX(8px)" : "translateX(0)",
                transition: "transform 0.35s ease",
              }}
            >
              {step.t}
            </h3>
          </div>

          <div style={{ width: 20, height: 20, position: "relative", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: "50%", left: 0, width: "100%", height: 1, background: "rgba(124,196,216,0.5)", transform: "translateY(-50%)" }} />
            <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 1, height: "100%" }}>
              <motion.div
                animate={{ scaleY: isOpen ? 0 : 1 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ width: "100%", height: "100%", background: "rgba(124,196,216,0.5)", transformOrigin: "center" }}
              />
            </div>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="content"
              initial={instant ? false : { height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={
                instant
                  ? { duration: 0 }
                  : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
              }
              style={{ overflow: "hidden" }}
            >
              <div style={{ padding: "1.5rem 2rem 1rem 2rem" }}>
                <p style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.95rem", color: "rgba(242,232,216,0.55)", lineHeight: 1.8, maxWidth: "60ch" }}>
                  {step.extended}
                </p>
                <ul style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem", listStyle: "none", padding: 0 }}>
                  {step.details.map((detail) => (
                    <li key={detail} style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <Check size={11} strokeWidth={2.5} style={{ color: "#7CC4D8", opacity: 0.8, flexShrink: 0 }} />
                      <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: "0.8rem", color: "rgba(242,232,216,0.45)" }}>
                        {detail}
                      </span>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: "1.5rem", display: "inline-flex", padding: "0.3rem 0.8rem", border: "1px solid rgba(124,196,216,0.2)", borderRadius: 2 }}>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: "0.7rem", color: "#7CC4D8", opacity: 0.6 }}>
                    {step.duration}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function Process() {
  const { lang } = useLang();
  const sectionRef = useRef(null);
  const stepRefs = useRef([]);
  const [openCount, setOpenCount] = useState(0);
  /** When true, expand steps without height animation (stable layout for nav scroll). */
  const [instantOpen, setInstantOpen] = useState(false);

  const steps = c.process.steps.map((s, i) => ({
    n:        String(i + 1).padStart(2, "0"),
    t:        s.t[lang],
    extended: s.extended[lang],
    details:  s.details[lang],
    duration: s.duration[lang],
  }));

  const { scrollYProgress: barProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });

  useEffect(() => {
    const handleScroll = () => {
      const triggerY = window.innerHeight * TRIGGER;
      let count = 0;
      stepRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top < triggerY) count = i + 1;
      });
      setOpenCount((prev) => Math.max(prev, count));
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* Nav / hash scroll: expand all steps instantly so #o-nas and later
     sections don't jump ~1000px mid-animation (accordion open = layout shift). */
  useEffect(() => {
    const prepare = () => {
      setInstantOpen(true);
      setOpenCount(steps.length);
    };
    window.addEventListener("przestrzen:prepare-scroll", prepare);
    return () => window.removeEventListener("przestrzen:prepare-scroll", prepare);
  }, [steps.length]);

  return (
    <section ref={sectionRef} id="proces" className="bg-navy-2 relative section-depth" data-testid="process-section">
      <div className="sticky top-0 left-0 right-0 pointer-events-none" style={{ height: 2, background: "rgba(242,232,216,0.05)", zIndex: 10 }}>
        <motion.div className="absolute inset-0 origin-left" style={{ scaleX: barProgress, background: "linear-gradient(90deg, #C87941, #7CC4D8)" }} />
      </div>

      <div className="section-pad relative overflow-hidden">
        <div className="pointer-events-none absolute" style={{ top: 0, left: 0, right: 0, height: 300, background: "radial-gradient(ellipse 80% 40% at 50% 0%, rgba(124,196,216,0.03), transparent)" }} />
        <div className="pointer-events-none absolute" style={{ bottom: 0, left: 0, right: 0, height: 200, background: "radial-gradient(ellipse 60% 30% at 30% 100%, rgba(200,121,65,0.04), transparent)" }} />

        <div className="max-w-7xl mx-auto relative">
          <div className="reveal mb-16 lg:mb-20 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <SectionLabel>{c.process.label[lang]}</SectionLabel>
              <SplitHeading
                line1={c.process.heading1[lang]}
                line2={<em className="italic text-amber">{c.process.ice[lang]}</em>}
                className="font-display font-light leading-[1.04] mt-5 text-white-warm"
                style={{ fontSize: "clamp(2.4rem, 4.8vw, 4rem)" }}
                data-testid="process-title"
              />
            </div>
            <SectionSubtitle className="text-white-dim max-w-xs">
              {c.process.subtitle[lang]}
            </SectionSubtitle>
          </div>

          <div>
            {steps.map((step, i) => (
              <StepItem
                key={step.n}
                step={step}
                isOpen={i < openCount}
                instant={instantOpen}
                stepRef={(el) => { stepRefs.current[i] = el; }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
